import { useSyncExternalStore } from "react";
import { useSelector } from "react-redux";

/**
 * 2 カラム（左サイドバー + 右コンテンツ）レイアウトへ切り替える最小ビューポート幅。
 * iPad 縦（768〜834px）を 2 カラムに含めつつ、スマホ縦と iPad mini 縦（744px）は
 * 1 カラムに落とす境界値。CSS メディアクエリと JS の分岐が食い違うと
 * 「サイドバーも開閉ボタンも出ない」デッドゾーンが生まれるため、
 * レイアウト分岐は必ずこの値（useIsTwoColumn）に一本化すること。
 */
export const TWO_COLUMN_BREAKPOINT = 768;

const TWO_COLUMN_QUERY = `(min-width: ${TWO_COLUMN_BREAKPOINT}px)`;

// matchMedia の結果はモジュールで 1 つだけ生成して全購読者で共有する
let mediaQueryList: MediaQueryList | null = null;
const getMediaQueryList = (): MediaQueryList | null => {
  if (typeof window === "undefined") return null;
  if (!mediaQueryList) {
    mediaQueryList = window.matchMedia(TWO_COLUMN_QUERY);
  }
  return mediaQueryList;
};

const subscribe = (onStoreChange: () => void): (() => void) => {
  const mql = getMediaQueryList();
  if (!mql) return () => {};
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
};

const getSnapshot = (): boolean => getMediaQueryList()?.matches ?? false;

/**
 * 2 カラムレイアウトで表示すべきかどうかの共通判定。
 *
 * - ビューポート幅が 768px 以上（matchMedia を購読し、リサイズ / 回転に追従）
 * - かつ UA がスマホ（phone）ではない
 *   （iPhone 横持ちは幅 768px を超え得るが 1 カラムを維持する。
 *    iPad / Android タブレットは幅が足りていれば PC と同じ 2 カラム）
 *
 * UA ベースの `state.device.isMobile` は hover / touch 有無の判定用で、
 * iPad も true になる。レイアウトの 1 / 2 カラム分岐にはこちらを使うこと。
 */
export const useIsTwoColumn = (): boolean => {
  const isPhone = useSelector(
    (state: { device: { isPhone: boolean } }) => state.device.isPhone,
  );
  const isWideViewport = useSyncExternalStore(subscribe, getSnapshot);
  return isWideViewport && !isPhone;
};

export default useIsTwoColumn;
